import { Component, Input, OnInit, SkipSelf, Optional, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { ControlContainer } from '@angular/forms';
import { ApplicationContextService } from '../services/application-context.service';
import { Subscription } from 'rxjs';
import { ApiConnectionsService } from '../services/api-connections.service';
import { ApiConnectionVo } from '../services/api-connection.do';
import { ApiConnectionTypeEnum } from '../designer-config-service/appconfig/app-config-designer';

@Component({
  selector: 'datex-common-api-connection-control',
  templateUrl: './api-connection-control.component.html',
  styleUrls: ['./api-connection-control.component.scss'],
  viewProviders: [{
    provide: ControlContainer,
    useFactory: (container: ControlContainer) => container,
    deps: [[new Optional(), new SkipSelf(), ControlContainer]],
  }],
  standalone: false
})
export class ApiConnectionControlComponent implements OnInit, OnChanges, OnDestroy {

  @Input() apiConnectionType: ApiConnectionTypeEnum;

  @Input() apiConnectionName: string;
  @Output() apiConnectionNameChange: EventEmitter<string> = new EventEmitter<string>();

  @Input() apiConnectionId: number;
  @Output() apiConnectionIdChange: EventEmitter<number> = new EventEmitter<number>();

  @Input() groupName = 'apiConnectionControlGroup';
  @Input() readOnly: boolean;


  apiConnections: ApiConnectionVo[];

  currentOrganizationSubscribtion$: Subscription;

  constructor(
    private applicationContextService: ApplicationContextService,
    private apiConnectionsService: ApiConnectionsService
  ) {
  }

  ngOnInit(): void {
    this.currentOrganizationSubscribtion$ = this.applicationContextService.currentOrganization$.subscribe(org => {
      if (org) {
        this.loadApiConnections();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.currentOrganizationSubscribtion$) {
      this.currentOrganizationSubscribtion$.unsubscribe();
    }
  }

  get currentOrganizationId(): number {
    return this.applicationContextService.getCurrentOrganization()?.id;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.organizationId || changes.apiConnectionType) {
      if (this.apiConnectionType) {
        this.loadApiConnections();
      } else {
        this.apiConnections = [];
      }
    }
  }

  get ready(): boolean {
    return !!(this.apiConnectionType && this.apiConnections);
  }

  loadApiConnections() {
    if (this.currentOrganizationId) {
      this.apiConnections = [];
      if (this.apiConnectionType) {
        this.apiConnectionsService.getApiConnections(this.apiConnectionType, this.currentOrganizationId)
          .subscribe(result => {
            this.apiConnections = result;
            if (this.apiConnectionId && !this.apiConnectionName) {
              const item = this.apiConnections.find(c => c.id === this.apiConnectionId);
              if (item) {
                this.apiConnectionName = item.name;
              }
            }
          });
      }
    }
  }

  apiConnectionNameChanged($event: string) {
    this.apiConnectionNameChange.emit($event);
    const item = this.apiConnections?.find(c => c.name === $event);
    this.apiConnectionId = item?.id ?? null;
    this.apiConnectionIdChange.emit(this.apiConnectionId);
  }

  get label(): string {
    switch (this.apiConnectionType) {
      case ApiConnectionTypeEnum.FootPrintApi:
        return 'Footprint API connection';
      case ApiConnectionTypeEnum.MongoDb:
        return 'MongoDb connection string';
      case ApiConnectionTypeEnum.Amqp:
        return 'Amqp connection string';
      case ApiConnectionTypeEnum.Sftp:
        return 'Sftp connection string';
      case ApiConnectionTypeEnum.Tcp:
        return 'Tcp connection string';
      case ApiConnectionTypeEnum.MsSql:
        return 'MSSQL connection';
      default:
        throw new Error(`Unknown ApiConnectionType ${this.apiConnectionType}`);
    }
  }

  getReadOnlyApiConnection(): string {
    if (this.ready) {
      const item = this.apiConnectionName
        ? this.apiConnections.find(c => c.name === this.apiConnectionName)
        : this.apiConnectionId
          ? this.apiConnections.find(c => c.id === this.apiConnectionId)
          : null;
      if (item) {
        return item.name + (item.readonly ? ' (Readonly)' : '');
      }
    }
    return null;
  }
}
